import { useCallback, useRef } from 'react'
import { convertValuesToStrings, transformFormDataForRegulator } from '../../utils/formDataTransformers'

function ImportExportSection({ formData, onImport, isLoading = false }) {
  const fileInputRef = useRef(null)

  const handleFileImport = useCallback((event) => {
    const file = event.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result)
        
        // Convert imported data to proper types for React Hook Form
        const cleanedData = Object.fromEntries(
          Object.entries(data).map(([key, value]) => {
            // Convert string "null" to empty string
            if (value === 'null' || value === null) {
              return [key, '']
            }
            // Convert string representations of booleans to actual booleans
            if (value === 'checked' || value === 'true') {
              return [key, true]
            }
            if (value === 'false') {
              return [key, false]
            }
            // Return value as-is for everything else
            return [key, value]
          }),
        )
        
        if (onImport) {
          onImport(cleanedData)
        }
      } catch (error) {
        console.error('Error parsing JSON file:', error)
        alert('Error: Invalid JSON file format')
      }
    }
    reader.readAsText(file)
    
    // Reset file input
    event.target.value = ''
  }, [onImport])
  // Generate timestamp for filename
  const generateTimestamp = useCallback(() => {
    const date = new Date()
    const formatNum = (num) => num.toString().padStart(2, '0')
    
    return `${formatNum(date.getDate())}-${formatNum(date.getMonth() + 1)}-${date.getFullYear()}-${formatNum(date.getHours())}${formatNum(date.getMinutes())}`
  }, [])

  const handleExport = useCallback(() => {
    if (!formData) {
      alert('No configuration data to export')
      return
    }

    // Use shared transformation utility for regulator.js compatibility
    // Exclude UI state fields to match HTML version behavior
    const exportData = transformFormDataForRegulator(formData, { 
      convertEmptyToNull: false, // Changed to false to prevent string "null" values
      excludeUiState: true, 
    })

    // Convert all values to strings for consistent JSON export using shared utility
    const stringifiedExportData = convertValuesToStrings(exportData)

    const filename = `Revatek config ${generateTimestamp()}.json`    // Create and download file
    const dataStr = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(stringifiedExportData, null, 4))}`
    const downloadAnchorNode = document.createElement('a')
    downloadAnchorNode.setAttribute('href', dataStr)
    downloadAnchorNode.setAttribute('download', filename)
    document.body.appendChild(downloadAnchorNode)
    downloadAnchorNode.click()
    downloadAnchorNode.remove()
  }, [formData, generateTimestamp])
  const triggerFileInput = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  // Reusable card renderer
  const renderCard = useCallback((config) => {
    const { icon, iconColor, title, description, button, helpText } = config
    
    return (
      <div className="border rounded p-3 h-100">
        <div className="d-flex align-items-center mb-2">
          <i className={`bi bi-${icon} text-${iconColor} me-2`}></i>
          <span className="fw-medium">{title}</span>
        </div>
        <p className="text-muted small mb-2">{description}</p>
        {button}
        <div className="mt-1">
          <small className="text-muted">{helpText}</small>
        </div>
      </div>
    )
  }, [])

  return (
    <div 
      className="card shadow-sm border-0 mb-4" 
      style={{ 
        background: 'linear-gradient(to right, #ffffff, #f8f9fa)',
        transition: 'all 0.3s ease',
      }}
    >
      {/* Card Header - Modern Design */}
      <div className="card-header fw-bold border-0 fs-5">
        <div className="d-flex align-items-center">
          <i className="bi bi-arrow-down-up text-info fs-3 me-2" aria-hidden="true"></i>
          <span>Import/Export Configuration</span>
        </div>
        <small className="text-muted">Backup and restore settings</small>
      </div>
      
      {/* Card Body */}
      <div className="card-body">
        <div className="row g-2">
          <div className="col-md-6">
          {renderCard({
            icon: 'upload',
            iconColor: 'primary',
            title: 'Import Configuration',
            description: 'Load a previously saved configuration file to restore settings.',
            button: (
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileImport}
                  accept=".json"
                  style={{ display: 'none' }}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={triggerFileInput}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1"></span>
                      Loading...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-upload me-1"></i> Choose File
                    </>
                  )}
                </button>
              </>
            ),
            helpText: 'Accepts JSON configuration files',
          })}
        </div>
        
        <div className="col-md-6">
          {renderCard({
            icon: 'download',
            iconColor: 'secondary',
            title: 'Export Configuration',
            description: 'Save your current configuration to a file for backup or transfer.',
            button: (
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={handleExport}
                disabled={!formData || isLoading}
              >
                <i className="bi bi-download me-1"></i> Export Current Config
              </button>
            ),
            helpText: 'Downloads a JSON file with timestamp',
          })}
        </div>
      </div>

      <div className="alert alert-info mt-3 py-2" role="alert">
        <small>
          <i className="bi bi-info-circle me-1"></i> 
          <strong>Important:</strong> Importing will overwrite all current settings. Always export your configuration before importing.
        </small>
        </div>
      </div>
    </div>
  )
}

export default ImportExportSection
